import { FC, useRef, useState } from 'react'
import { CustomIcon } from '../../components/customIcon/customIcon'
import { useMediaUrl } from '../../hooks/useMediaUrl'
import classes from '../../styles/voices/voiceCard.module.scss'
import cn from 'classnames'

const LANG_LABELS: Record<string, { short: string; full: string }> = {
  ru: { short: 'ру', full: 'Русский' },
  en: { short: 'en', full: 'English' },
  de: { short: 'de', full: 'Deutsch' },
  fr: { short: 'fr', full: 'Français' },
  es: { short: 'es', full: 'Español' },
  it: { short: 'it', full: 'Italiano' },
  zh: { short: 'zh', full: 'Chinese' },
  kk: { short: 'кз', full: 'Қазақша' },
  tt: { short: 'тт', full: 'Татарча' },
}

const getLang = (lang: string) =>
  LANG_LABELS[lang.toLowerCase()] ?? { short: lang.toLowerCase(), full: lang }

export const VoiceCard: FC<{
  name?: string
  gender?: 'male' | 'female'
  lang?: string
  avatar?: string
  blur?: boolean
  isPreview?: boolean
  previewAudio?: string | null
  className?: string
  onClick?: () => void
  selected?: boolean
}> = ({ name, gender, lang, avatar, blur, previewAudio, className, onClick, selected }) => {
  const isLocalAudio = previewAudio?.startsWith('blob:') ?? false
  const isLocalAvatar = avatar?.startsWith('blob:') ?? false
  const remoteAvatarSrc = useMediaUrl(isLocalAvatar ? null : avatar)
  const avatarSrc = isLocalAvatar ? avatar : remoteAvatarSrc
  const remoteAudioSrc = useMediaUrl(isLocalAudio ? null : previewAudio)
  const audioSrc = isLocalAudio ? previewAudio : remoteAudioSrc
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation()
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play()
      setIsPlaying(true)
    }
  }

  return (
    <div className={cn(classes.card, blur && classes.cardBlur, selected && classes.cardSelected, className)} onClick={onClick}>
      <div className={classes.cardAvatar}>
        {avatarSrc
          ? <img src={avatarSrc} alt={name} className={classes.cardAvatarImg} />
          : !blur ? <CustomIcon icon="userIcon" /> : null
        }
      </div>
      <div className={classes.cardFooter}>
        <div className={classes.cardInfo}>
          <span className={classes.cardName}>{name || 'Имя'}</span>
          <span className={cn(classes.cardGender, gender === 'female' ? classes.genderFemale : gender === 'male' ? classes.genderMale : classes.genderUnknown)}>
            {gender ? <CustomIcon icon={gender === 'female' ? 'female' : 'male'} /> : '?'}
          </span>
          <span className={cn(classes.cardLang, !lang && classes.cardLangUnknown)} title={lang ? getLang(lang).full : undefined}>
            {lang ? getLang(lang).short : '?'}
          </span>
        </div>
        {audioSrc && (
          <>
            <audio
              ref={audioRef}
              src={audioSrc}
              onEnded={() => setIsPlaying(false)}
            />
            <button className={cn(classes.cardPlay, classes.cardPlayActive)} onClick={handlePlayPause}>
              <CustomIcon icon={isPlaying ? 'pause' : 'play'} />
            </button>
          </>
        )}
      </div>
    </div>
  )
}
